import { html, View } from 'rune-ts';
import klass from './RecentlySoldProductSection.module.scss';
import { ProductCard, ProductLikeToggleEvent } from '../../../../components/cells/ProductCard/ProductCard';
import { ProductCardData, ProductCardSize, ProductCardTheme } from '../../../../../features/ProductList/type';
import { getResizedUrl } from '../../../../../shared/util/image';
import { staticTypo, typo } from '../../../../../shared/typography/typo';
import { ProductStoreName } from '../../../../components/cells/ProductCard/view/ProductStoreName/ProductStoreName';
import { ProductCardPrice } from '../../../../components/cells/ProductCardPrice/ProductCardPrice';
import { MShopThinIcon } from '../../../../components/atoms/Icon';
import { CreatorConstantS } from '../../../../../features/Creator/outbound/share';
import { SwiperChangeEvent, SwiperView } from '../../../../components/cells/Swiper/Swiper';
import { generateProductListUrl, ShopPage } from '../../../../../features/ProductList/util';
import { makeShopScreenNavigate } from '../../../../../shared/app/navigate';
import { ProductCardName } from '../../../../components/cells/ProductCard/view/ProductCardName/ProductCardName';
import { SectionTitle } from '@services/MarppleShop/renderApp/components/cells/SectionTitle/SectionTitle.ts';
import { SeeMoreLinkPlugin } from '@services/MarppleShop/renderApp/components/cells/SectionTitle/plugins';
import { ProductTagFactory } from '@services/MarppleShop/renderApp/components/cells/ProductTag/ProductTagFactory.ts';
import {
  ProductTagAlign,
  ProductTagOption,
  ProductTagSize,
} from '@services/MarppleShop/renderApp/components/cells/ProductTag/ProductTagBase.ts';
import { ProductCardList } from '@services/MarppleShop/renderApp/components/cells/ProductCardList/ProductCardList';

const MO_PRODUCT_CARD_OPTION = {
  resize_width: 600,
  size: ProductCardSize.medium,
  klass: klass.product_card,
  hide_tag: true,
  hide_review: true,
  theme: ProductCardTheme.dark,
} as const;

const MO_SWIPER_OPTION = {
  autoplay: false,
  slideClass: klass.product_card,
  slidesPerView: 'auto',
  speed: 600,
} as const;

export interface RecentlySoldProductSectionData {
  products: ProductCardData[];
  banner_images: string[];
}

interface RecentlySoldProductSectionOption {
  is_mobile: boolean;
}

export class RecentlySoldProductSection extends View<RecentlySoldProductSectionData> {
  content_swiper_view: SwiperView<ProductCardData, typeof ProductCard>;

  private localized_text: ReturnType<typeof this.getLocalizedText>;

  constructor(
    data: RecentlySoldProductSectionData,
    private option: RecentlySoldProductSectionOption,
  ) {
    super(data, option);
    this.localized_text = this.getLocalizedText();

    this.content_swiper_view = new SwiperView(
      { ...MO_SWIPER_OPTION, init: option.is_mobile },
      {
        swiper: klass.product_cards_swiper,
        wrapper: klass.product_cards_container,
      },
      ProductCard,
      this.data.products,
      MO_PRODUCT_CARD_OPTION,
    );
  }

  override template() {
    if (!RecentlySoldProductSection.isVisible(this.data)) {
      return html``;
    }

    const { is_mobile } = this.option;
    const see_more_link = generateProductListUrl({
      page: ShopPage.recently_sold,
    });

    return html`<section class="${klass.section}">
      <img
        class="${klass.background_image}"
        src="${getResizedUrl({
          url: this.getBannerImageByIndex(0),
          quality: 70,
          format: 'webp',
        })}"
        alt=""
      />
      <div class="${klass.blur_backdrop}"></div>
      <div class="${klass.content_wrapper}">
        <div class="${klass.content}">
          <div class="${klass.section_title}">
            ${new SectionTitle({
              title_props: {
                title: this.localized_text.title,
                sub_title: this.localized_text.sub_title,
                size: is_mobile ? 'large' : 'xLarge',
                theme: 'dark',
              },
              plugins: [
                new SeeMoreLinkPlugin({
                  navigate: makeShopScreenNavigate(see_more_link),
                  see_more_link,
                  size: is_mobile ? 'xSmall' : 'medium',
                  theme: 'dark',
                }),
              ],
            })}
          </div>
          ${is_mobile ? this.content_swiper_view : this.makePcProductCardsContainerTmpl()}
        </div>
      </div>
    </section>`;
  }

  override onRender() {
    this.delegate(ProductLikeToggleEvent, ProductCard, async (_, view) => {
      const [latest] = this.data.products;
      if (latest && latest.id == view.data.id) {
        await view.toggleLikeButton();
      }
    });

    if (this.option.is_mobile) {
      this.delegate(SwiperChangeEvent, SwiperView, (e) => {
        const { current_index } = e.detail;
        this.setBannerImageToBackground(this.getBannerImageByIndex(current_index));
      });
    }
  }

  private getBannerImageByIndex(index: number): string {
    return this.data.banner_images[index] || CreatorConstantS.banner_img;
  }

  private setBannerImageToBackground(image_url: string) {
    const image_el = this.element().querySelector(`.${klass.background_image}`);
    if (!image_el) {
      return;
    }
    image_el.setAttribute('src', image_url);
  }

  static isVisible(data: RecentlySoldProductSectionData): boolean {
    return data.products.length >= 1;
  }

  private makePcProductCardsContainerTmpl() {
    const [latest, ...others] = this.data.products;
    const { is_mobile } = this.option;

    if (!latest) {
      return html``;
    }

    return html`
      <div class="${klass.product_cards_container}">
        ${new ProductCard(latest, {
          resize_width: 600,
          size: ProductCardSize.large,
          theme: ProductCardTheme.dark,
          hide_info: !is_mobile,
          klass: `${klass.product_card} ${klass.latest}`,
        })}
        <div class="${klass.right}">
          <div class="${klass.latest_info}">
            ${new ProductStoreName(latest, {
              klass: `${typo('16_bold')} ${klass.store_name}`,
              suffix: html`<span class="${klass.icon}">${MShopThinIcon}</span>`,
            })}
            ${new ProductCardName(latest, {
              klass: `${klass.product_name} ${typo('20_medium')}`,
            })}
            <div class="${klass.product_price}">
              ${new ProductCardPrice(latest, {
                price_klass: staticTypo('unica_20_bold'),
                currency_klass: typo('20_bold'),
              })}
            </div>
            ${latest.ordered_at
              ? html`<div class="${klass.realtime_tag}">
                  ${ProductTagFactory.create('realtime', new Date(latest.ordered_at), {
                    lang: ET.lang,
                    size: ProductTagSize.large,
                    align: ProductTagAlign.center,
                    options: [ProductTagOption.stretch],
                  })}
                </div>`
              : ''}
          </div>
          ${new ProductCardList(
            { products: others },
            {
              resize_width: 600,
              size: ProductCardSize.medium,
              klass: `${klass.product_card}`,
              hide_tag: true,
              hide_review: true,
              theme: ProductCardTheme.dark,
              grid: {
                column_gap: 16,
                columns: 4,
              },
              limit: 4,
            },
          )}
        </div>
      </div>
    `;
  }

  private getLocalizedText() {
    return {
      title: this.option.is_mobile ? ET('mps2::main::realtime::title_mo') : ET('mps2::main::realtime::title'),
      sub_title: ET('mps2::main::realtime::sub_title'),
    };
  }
}
